export type InputState = {
  forward: boolean;
  backward: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
  sprint: boolean;
  attack: boolean; // left click
  interact: boolean; // right click
  mouseDeltaX: number;
  mouseDeltaY: number;
};

export type InputManager = {
  state: InputState;
  isPointerLocked: boolean;
  requestPointerLock: () => void;
  resetMouseDelta: () => void;
  dispose: () => void;
};

export function createInputManager(canvas: HTMLCanvasElement): InputManager {
  const state: InputState = {
    forward: false,
    backward: false,
    left: false,
    right: false,
    jump: false,
    sprint: false,
    attack: false,
    interact: false,
    mouseDeltaX: 0,
    mouseDeltaY: 0,
  };

  let isPointerLocked = false;

  type BooleanKey = "forward" | "backward" | "left" | "right" | "jump";

  const keyMap: Record<string, BooleanKey> = {
    KeyW: "forward",
    KeyS: "backward",
    KeyA: "left",
    KeyD: "right",
    Space: "jump",
  };

  // Double-W sprint detection
  let lastWPress = 0;
  const DOUBLE_TAP_WINDOW = 300; // ms

  function handleKeyDown(e: KeyboardEvent) {
    // Double-W sprint
    if (e.code === "KeyW") {
      const now = performance.now();
      if (now - lastWPress < DOUBLE_TAP_WINDOW && !state.forward) {
        state.sprint = true;
      }
      lastWPress = now;
    }

    // Shift also sprints
    if (e.code === "ShiftLeft") {
      state.sprint = true;
    }

    const action = keyMap[e.code];
    if (action) {
      state[action] = true;
      e.preventDefault();
    }
  }

  function handleKeyUp(e: KeyboardEvent) {
    // Release sprint when W released (double-W sprint)
    if (e.code === "KeyW") {
      state.sprint = false;
    }
    if (e.code === "ShiftLeft") {
      // Only stop sprint if W is not held (for Shift sprint)
      if (!state.forward) {
        state.sprint = false;
      }
    }

    const action = keyMap[e.code];
    if (action) {
      state[action] = false;
    }
  }

  function handleMouseMove(e: MouseEvent) {
    if (!isPointerLocked) return;
    state.mouseDeltaX += e.movementX;
    state.mouseDeltaY += e.movementY;
  }

  function handleMouseDown(e: MouseEvent) {
    if (!isPointerLocked) {
      requestPointerLock();
      return;
    }
    if (e.button === 0) state.attack = true;
    if (e.button === 2) state.interact = true;
  }

  function handleMouseUp(e: MouseEvent) {
    if (e.button === 0) state.attack = false;
    if (e.button === 2) state.interact = false;
  }

  function handleContextMenu(e: Event) {
    e.preventDefault();
  }

  function handlePointerLockChange() {
    isPointerLocked = document.pointerLockElement === canvas;
  }

  function requestPointerLock() {
    canvas.requestPointerLock();
  }

  // Bind events
  document.addEventListener("keydown", handleKeyDown);
  document.addEventListener("keyup", handleKeyUp);
  document.addEventListener("mousemove", handleMouseMove);
  canvas.addEventListener("mousedown", handleMouseDown);
  document.addEventListener("mouseup", handleMouseUp);
  // Right-click is a game action everywhere — the browser context menu
  // must never open (it hijacked interactions with blocks/UI).
  document.addEventListener("contextmenu", handleContextMenu);
  document.addEventListener("pointerlockchange", handlePointerLockChange);

  return {
    state,
    get isPointerLocked() {
      return isPointerLocked;
    },
    requestPointerLock,
    resetMouseDelta() {
      state.mouseDeltaX = 0;
      state.mouseDeltaY = 0;
    },
    dispose() {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      document.removeEventListener("mousemove", handleMouseMove);
      canvas.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("pointerlockchange", handlePointerLockChange);
    },
  };
}
